"use client";

import type { BentoGridItemSize, BentoGridTextPlacement } from ".";
import type { CSSProperties } from "react";
import type { ImageProps } from "next/image";

import Image from "next/image";
import {
  useAnimationFrame,
  useInView,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
  type MotionValue,
} from "motion/react";
import * as m from "motion/react-m";
import { clsx } from "clsx";
import { useEffect, useRef } from "react";
import { radiansToDegrees } from "@ui/lib/math";

const MIN_IMAGE_COUNT = 3;
const MAX_IMAGE_COUNT = 12;
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const ACTIVE_SPIN_SPEED = 0.58;
const IDLE_SPIN_SPEED = 0.1;
const ACTIVE_PITCH_SPEED = 0.38;

const ORB_CONFIG = {
  small: {
    orbSize: "17rem",
    cardSize: "4.5rem",
    radius: "6.75rem",
  },
  medium: {
    orbSize: "21rem",
    cardSize: "5.25rem",
    radius: "8.5rem",
  },
  large: {
    orbSize: "25rem",
    cardSize: "6.5rem",
    radius: "10.125rem",
  },
  extraLarge: {
    orbSize: "27.5rem",
    cardSize: "6.75rem",
    radius: "11.125rem",
  },
} as const;

type OrbPoint = {
  latDeg: number;
  lonDeg: number;
  vector: {
    x: number;
    y: number;
    z: number;
  };
};

function getOrbConfig(size: BentoGridItemSize) {
  return size ? ORB_CONFIG[size] : ORB_CONFIG.medium;
}

function createOrbPoints(count: number) {
  return Array.from({ length: count }, (_, index): OrbPoint => {
    const y = 1 - (index / (count - 1)) * 2;
    const ringRadius = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = index * GOLDEN_ANGLE;
    const x = Math.cos(theta) * ringRadius;
    const z = Math.sin(theta) * ringRadius;

    return {
      latDeg: radiansToDegrees(Math.asin(y)),
      lonDeg: radiansToDegrees(Math.atan2(x, z)),
      vector: { x, y, z },
    };
  });
}

function getRotatedZ(point: OrbPoint["vector"], spinX: number, spinY: number) {
  const cosY = Math.cos(spinY);
  const sinY = Math.sin(spinY);
  const cosX = Math.cos(spinX);
  const sinX = Math.sin(spinX);
  const zAfterY = point.z * cosY - point.x * sinY;

  return point.y * sinX + zAfterY * cosX;
}

export function CardsOrbDemo({
  images = [],
  size,
  textPlacement,
}: {
  images?: ImageProps[];
  size: BentoGridItemSize;
  textPlacement: BentoGridTextPlacement;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const spinY = useMotionValue(-0.55);
  const pitchPhase = useMotionValue(0.7);
  const speedTarget = useMotionValue(IDLE_SPIN_SPEED);
  const spinSpeed = useSpring(speedTarget, {
    stiffness: 28,
    damping: 24,
    mass: 1.4,
  });
  const spinX = useTransform(pitchPhase, phase => -0.17 + Math.sin(phase) * 0.28);
  const isInView = useInView(containerRef, { amount: 0.2 });
  const reducedMotion = useReducedMotion() ?? false;
  const orbImages = images.slice(0, MAX_IMAGE_COUNT);

  useEffect(() => {
    speedTarget.set(reducedMotion ? 0 : IDLE_SPIN_SPEED);
  }, [reducedMotion, speedTarget]);

  useAnimationFrame((_time, delta) => {
    if (reducedMotion || !isInView) {
      return;
    }

    const elapsed = Math.min(Math.max(delta / 1000, 0), 0.05);
    const currentSpinSpeed = spinSpeed.get();
    const pitchSpeed = (currentSpinSpeed / ACTIVE_SPIN_SPEED) * ACTIVE_PITCH_SPEED;

    spinY.set(spinY.get() + elapsed * currentSpinSpeed);
    pitchPhase.set(pitchPhase.get() + elapsed * pitchSpeed);
  });

  if (orbImages.length < MIN_IMAGE_COUNT) {
    return null;
  }

  const orbConfig = getOrbConfig(size);
  const orbPoints = createOrbPoints(orbImages.length);

  return (
    <m.div
      ref={containerRef}
      className='tw-flex tw-h-full tw-w-full tw-select-none tw-items-center tw-justify-center'
      onHoverStart={() => {
        if (!reducedMotion) {
          speedTarget.set(ACTIVE_SPIN_SPEED);
        }
      }}
      onHoverEnd={() => {
        speedTarget.set(reducedMotion ? 0 : IDLE_SPIN_SPEED);
      }}
      aria-hidden
    >
      <div
        className={clsx(
          "tw-grid tw-place-items-center [perspective:56.25rem]",
          textPlacement === "top" ? "tw-translate-y-2" : "-tw-translate-y-4",
        )}
        style={
          {
            "--orb-size": orbConfig.orbSize,
            "--card-size": orbConfig.cardSize,
            "--radius": orbConfig.radius,
          } as CSSProperties
        }
      >
        <div className='tw-relative tw-size-[var(--orb-size)] [transform-style:preserve-3d]'>
          {orbImages.map((image, index) => (
            <OrbCard image={image} key={`${image.src}-${index}`} point={orbPoints[index]} spinX={spinX} spinY={spinY} />
          ))}
        </div>
      </div>
    </m.div>
  );
}

function OrbCard({
  image,
  point,
  spinX,
  spinY,
}: {
  image: ImageProps;
  point: OrbPoint;
  spinX: MotionValue<number>;
  spinY: MotionValue<number>;
}) {
  const transform = useTransform([spinX, spinY], ([spinXValue, spinYValue]: number[]) => {
    const spinXDeg = radiansToDegrees(spinXValue);
    const spinYDeg = radiansToDegrees(spinYValue);

    return [
      "translate(-50%, -50%)",
      `rotateY(${spinYDeg}deg)`,
      `rotateX(${spinXDeg}deg)`,
      `rotateY(${point.lonDeg}deg)`,
      `rotateX(${point.latDeg}deg)`,
      "translateZ(var(--radius))",
      `rotateX(${-point.latDeg}deg)`,
      `rotateY(${-point.lonDeg}deg)`,
      `rotateX(${-spinXDeg}deg)`,
      `rotateY(${-spinYDeg}deg)`,
    ].join(" ");
  });

  const depth = useTransform(
    [spinX, spinY],
    ([spinXValue, spinYValue]: number[]) => (getRotatedZ(point.vector, spinXValue, spinYValue) + 1) / 2,
  );
  const scale = useTransform(depth, depthValue => 0.72 + depthValue ** 1.2 * 0.46);
  const zIndex = useTransform(depth, depthValue => Math.round(depthValue * 1000));

  return (
    <m.div
      className='tw-absolute tw-left-1/2 tw-top-1/2 tw-size-[var(--card-size)] tw-will-change-transform [transform-style:preserve-3d]'
      style={{ transform, zIndex }}
    >
      <m.div
        className='tw-size-full tw-origin-center tw-overflow-hidden tw-rounded-lg [backface-visibility:hidden]'
        style={{ scale }}
      >
        <Image {...image} alt='' className='tw-size-full tw-object-cover' draggable={false} sizes='7rem' />
      </m.div>
    </m.div>
  );
}
